import React from 'react'
import styled from 'styled-components'
// @ts-ignore
import { StarSolid, Tooltip } from '@netbasequid/core'
import { Theme } from 'features/ui/types'
import { useOverflowObserver } from 'hooks/useOverflowObserver'
import { useDisplayTheme } from 'hooks/useUiState'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
// @ts-ignore
import { sys } from '@netbasequid/theme'

type TextColor = 'weak' | 'placeholder' | 'primary'

export const InsightChartContainer = styled.div`
  width: 100%;
`

interface InsightContainerProps {
  displayType?: 'average' | 'total'
  mode: Theme
  clickable?: boolean
}

const getTestIdByDisplayType = (displayType: InsightContainerProps['displayType']) => {
  switch (displayType) {
    case 'average':
      return AutomationId.insightChart.average
    case 'total':
      return AutomationId.insightChart.total
    default:
      return AutomationId.insightChart.insight
  }
}

export const InsightContainer = styled.div.attrs<InsightContainerProps>(({ displayType }) => ({
  [AUTO_ATTR_KEY]: getTestIdByDisplayType(displayType)
}))<InsightContainerProps>`
  margin-bottom: 4px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'cursor')};
  &:last-child {
    margin: 0;
  }
  &:hover {
    background-color: ${({ mode, clickable }) =>
      clickable ? sys[mode].interaction.onElevation.default : ''};
  }
  &:active {
    background-color: ${({ mode, clickable }) =>
      clickable ? sys[mode].interaction.onElevation.pressed : ''};
  }
`

interface CountContainerProps {
  displayType?: 'average' | 'total'
}

export const CountContainer = styled.div.attrs<CountContainerProps>(({ displayType }) => ({
  [AUTO_ATTR_KEY]: getTestIdByDisplayType(displayType)
}))<CountContainerProps>`
  margin-bottom: 4px;
  &:last-child {
    margin: 0;
  }
`

export const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

interface LabelNameProps {
  hasTestId?: boolean
}
export const LabelName = styled.div.attrs<LabelNameProps>(({ hasTestId = true }) => ({
  [AUTO_ATTR_KEY]: hasTestId ? AutomationId.insightChart.label : undefined
}))<LabelNameProps>`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

interface StyledLabelValueProps {
  type?: TextColor
  mode: Theme
}

const StyledLabelValue = styled.div.attrs<StyledLabelValueProps>({
  [AUTO_ATTR_KEY]: AutomationId.insightChart.value
})<StyledLabelValueProps>`
  margin-left: 16px;
  font-size: 14px;
  font-weight: ${({ type }) => (type === 'weak' ? 400 : 600)};
  line-height: 22px;
  color: ${({ mode, type }) => sys[mode].text[type]};
`

export const LabelValue = ({
  children,
  type = 'primary'
}: {
  children: React.ReactNode
  type?: StyledLabelValueProps['type']
}) => {
  const theme = useDisplayTheme()
  return (
    <StyledLabelValue mode={theme} type={type}>
      {children}
    </StyledLabelValue>
  )
}

const StarContainer = styled.div`
  margin-left: 4px;
  display: inline;
`

export const Star = ({ mode }: { mode: Theme }) => (
  <StarContainer>
    <StarSolid color={sys[mode].text.primary} fontSize={12} />
  </StarContainer>
)

interface LabelNameInnerProps {
  type?: TextColor
  mode: Theme
}
const LabelNameInner = styled.div.attrs<LabelNameInnerProps>({
  [AUTO_ATTR_KEY]: AutomationId.insightChart.label
})<LabelNameInnerProps>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ mode, type }) => sys[mode].text[type]};
  pointer-events: none; // * prevent browser Tooltip over Truncated Text in Safari
`

export const LabelNameWithTooltip = ({
  children,
  type = 'primary'
}: {
  children: React.ReactNode
  type?: LabelNameInnerProps['type']
}) => {
  const theme = useDisplayTheme()
  const { isOverflowX, targetRef } = useOverflowObserver()

  return (
    <Tooltip enabled={isOverflowX} content={children} mode={theme}>
      <LabelName hasTestId={false}>
        <LabelNameInner
          type={type}
          mode={theme}
          ref={targetRef as React.MutableRefObject<HTMLDivElement>}>
          {children}
        </LabelNameInner>
      </LabelName>
    </Tooltip>
  )
}
